/**
 * Service for managing customer comments JSON structure
 * Comments are stored as: { comments: [{ id, comment, email, datetime }] }
 */

export interface CustomerComment {
  id: string;
  comment: string;
  email: string;
  name?: string; // User's display name
  datetime: string;
  originatorCollection?: number; // Collection ID where the comment was created
}

export interface CustomerCommentsData {
  comments: CustomerComment[];
}

/**
 * Generate a unique ID for a comment
 */
function generateCommentId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse customer comment JSON string into structured data
 */
export function parseCustomerComments(json: string | undefined | null): CustomerCommentsData {
  if (!json || typeof json !== 'string') return { comments: [] };

  const trimmed = json.trim();
  if (!trimmed) return { comments: [] };

  try {
    const parsed = JSON.parse(trimmed);

    // Handle array format: [{ comment, email, datetime }]
    if (Array.isArray(parsed)) {
      return {
        comments: parsed.map((c) => ({
          id: generateCommentId(), // Add ID if missing
          comment: typeof c.comment === 'string' ? c.comment : '',
          email: typeof c.email === 'string' ? c.email : '',
          datetime: typeof c.datetime === 'string' ? c.datetime : new Date().toISOString(),
          originatorCollection: typeof c.originatorCollection === 'number' ? c.originatorCollection : undefined,
          name: typeof c.name === 'string' ? c.name : undefined,
        })),
      };
    }

    // Handle object format: { comments: [{ comment, email, datetime }] }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.comments)) {
      return {
        comments: parsed.comments.map((c: unknown) => {
          if (typeof c === 'object' && c !== null) {
            const obj = c as Record<string, unknown>;
            return {
              id: typeof obj.id === 'string' ? obj.id : generateCommentId(),
              comment: typeof obj.comment === 'string' ? obj.comment : '',
              email: typeof obj.email === 'string' ? obj.email : '',
              datetime: typeof obj.datetime === 'string' ? obj.datetime : new Date().toISOString(),
              originatorCollection: typeof obj.originatorCollection === 'number' ? obj.originatorCollection : undefined,
              name: typeof obj.name === 'string' ? obj.name : undefined,
            };
          }
          return {
            id: generateCommentId(),
            comment: '',
            email: '',
            datetime: new Date().toISOString(),
            originatorCollection: undefined,
            name: undefined,
          };
        }),
      };
    }

    // Handle single comment format: { comment, email, datetime }
    if (parsed && typeof parsed === 'object' && 'comment' in parsed) {
      const obj = parsed as Record<string, unknown>;
      return {
        comments: [
          {
            id: typeof obj.id === 'string' ? obj.id : generateCommentId(),
            comment: typeof obj.comment === 'string' ? obj.comment : '',
            email: typeof obj.email === 'string' ? obj.email : '',
            datetime: typeof obj.datetime === 'string' ? obj.datetime : new Date().toISOString(),
            originatorCollection: typeof obj.originatorCollection === 'number' ? obj.originatorCollection : undefined,
            name: typeof obj.name === 'string' ? obj.name : undefined,
          },
        ],
      };
    }

    return { comments: [] };
  } catch {
    return { comments: [] };
  }
}

/**
 * Serialize customer comments data to JSON string
 */
export function serializeCustomerComments(data: CustomerCommentsData): string {
  if (!data.comments || data.comments.length === 0) return '';
  return JSON.stringify(data);
}

/**
 * Add a new comment to the comments data
 */
export function addComment(
  data: CustomerCommentsData,
  comment: string,
  email: string,
  datetime: string = new Date().toISOString(),
  originatorCollection?: number,
  name?: string,
): CustomerCommentsData {
  return {
    comments: [
      ...data.comments,
      {
        id: generateCommentId(),
        comment: comment.trim(),
        email: email.trim(),
        datetime,
        originatorCollection,
        name,
      },
    ],
  };
}

/**
 * Update an existing comment by ID
 */
export function updateComment(data: CustomerCommentsData, id: string, updates: Partial<Omit<CustomerComment, 'id'>>): CustomerCommentsData {
  return {
    comments: data.comments.map((c) =>
      c.id === id
        ? {
            ...c,
            ...updates,
            comment: updates.comment !== undefined ? updates.comment.trim() : c.comment,
            email: updates.email !== undefined ? updates.email.trim() : c.email,
          }
        : c,
    ),
  };
}

/**
 * Delete a comment by ID
 */
export function deleteComment(data: CustomerCommentsData, id: string): CustomerCommentsData {
  return {
    comments: data.comments.filter((c) => c.id !== id),
  };
}

/**
 * Format a single comment for display
 */
export function formatCommentForDisplay(comment: CustomerComment): string {
  let prefix = '';

  if (comment.email || comment.datetime) {
    let timeStr = '';
    if (comment.datetime) {
      const d = new Date(comment.datetime);
      if (!isNaN(d.valueOf())) {
        try {
          timeStr = new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Europe/Oslo',
          }).format(d);
        } catch {
          timeStr = d.toISOString();
        }
      } else {
        timeStr = comment.datetime;
      }
    }

    const parts: string[] = [];
    if (comment.email) parts.push(comment.email);
    if (timeStr) parts.push(timeStr);
    prefix = parts.length ? `(${parts.join(' - ')})` : '';
  }

  return prefix ? `${prefix}\n${comment.comment}` : comment.comment;
}

/**
 * Format all comments for display (legacy compatibility)
 */
export function formatAllCommentsForDisplay(json: string | undefined | null): string {
  const data = parseCustomerComments(json);
  const texts = data.comments.map(formatCommentForDisplay).filter((t) => t && t.trim().length > 0);

  return texts.join('\n\n');
}
